/**
 * iter84.56.8 — Tech-debt split from ReleaseOpsAuditCards.jsx.
 * Mechanical extraction; no behavior change.
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
    RefreshCw, AlertTriangle, CheckCircle2, XCircle,
    Loader2, ChevronDown, ChevronRight, ClipboardCheck, 
    
} from "lucide-react";
import { CollapsibleCard } from "@/components/site-admin/CollapsibleCard";
import { VERDICT_STYLE, VerdictIcon } from "@/components/site-admin/audit-cards/_shared";

export function PreflightAttestationLedgerCard({ environment = "preview" }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);
    const [openIter, setOpenIter] = useState(null);
    // iter84.56.7-h — Last 10 inline + "View all" modal for full history.
    const [showAll, setShowAll] = useState(false);

    const fetchOnce = useCallback(async (showSpinner) => {
        if (showSpinner) setRefreshing(true);
        try {
            const r = await api.releaseOpsPreflightAttestations();
            setData(r);
            setError(null);
        } catch (e) {
            setError(e.response?.data?.detail || "Failed to load attestation ledger");
        } finally {
            setRefreshing(false);
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchOnce(false); }, [fetchOnce]);

    const copyUatUrl = (iter) => {
        const url = `${window.location.origin}/api/release-ops/uat/${iter}`;
        navigator.clipboard.writeText(url).then(
            () => toast.success(`UAT URL for iter${iter} copied`),
            () => toast.error("Copy failed"),
        );
    };

    const total = data?.count ?? 0;
    const attested = data?.attested_count ?? 0;
    // iter86.82.2 — outline verdicts removed from Phase 4 cards (gray
    // borders); the per-iter verdict computation went with them (iter86.83).

    const refreshBtn = (
        <span
            role="button"
            tabIndex={0}
            onClick={(e) => { e.stopPropagation(); fetchOnce(true); }}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); fetchOnce(true); } }}
            data-testid="preflight-attestation-refresh-btn"
            className="brutal-btn-ghost text-[10px] py-1 px-2 inline-flex items-center gap-1 cursor-pointer"
        >
            {refreshing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            Refresh
        </span>
    );

    return (
        // iter86.82.2 — no outline verdict: Phase 4 audit cards keep the
        // standard gray border (operator consistency ask); the signal
        // lives in the badge + per-row VerdictIcons.
        <CollapsibleCard
            testId="preflight-attestation-ledger-card"
            icon={ClipboardCheck}
            title="Agent pre-flight ledger"
            badge={data ? `${attested}/${total} attested` : (environment === "production" ? "production" : "preview")}
            badgeClass="bg-zinc-700/30 text-zinc-300 border-zinc-600"
            headerExtra={refreshBtn}
        >
            <div data-environment={environment} data-total={total} data-attested={attested}>
                {loading && (
                    <p className="text-xs text-zinc-500 italic flex items-center gap-2">
                        <Loader2 className="w-3 h-3 animate-spin" /> Loading ledger…
                    </p>
                )}
                {error && (
                    <p className="text-xs text-rose-400" data-testid="preflight-attestation-error">
                        {error}
                    </p>
                )}
                {!loading && !error && data && (
                    <>
                        <p className="text-[11px] text-zinc-400 leading-relaxed mb-3">
                            Per-iter audit of the Pre-flight (agent-attested) block in
                            each <code className="text-[10px] bg-zinc-800 px-1 rounded">UAT_iter*.md</code>.
                            Locks pre-deploy verifications to a parseable record so future drift never silently slips by.
                        </p>
                        <ul className="space-y-1" data-testid="preflight-attestation-rows">
                            {(data.rows.slice(0, 10)).map((row) => {
                                const style = VERDICT_STYLE[row.verdict] || VERDICT_STYLE.missing;
                                const isOpen = openIter === row.iter;
                                return (
                                    <li
                                        key={row.iter}
                                        data-testid={`preflight-attestation-row-${row.iter}`}
                                        data-verdict={row.verdict}
                                        data-attested={row.attested ? "true" : "false"}
                                        className={`border rounded-sm transition-colors ${
                                            row.attested
                                                ? "border-zinc-800 bg-zinc-900/30"
                                                : "border-zinc-800 bg-zinc-900/30 opacity-80"
                                        }`}
                                    >
                                        <button
                                            type="button"
                                            onClick={() => setOpenIter(isOpen ? null : row.iter)}
                                            className="w-full flex items-center justify-between gap-2 text-left py-1.5 px-2"
                                            data-testid={`preflight-attestation-row-${row.iter}-toggle`}
                                        >
                                            <div className="flex items-center gap-2 flex-wrap min-w-0">
                                                <VerdictIcon verdict={row.verdict} />
                                                <span className="font-mono text-[11px] text-white font-bold">
                                                    iter{row.iter}
                                                </span>
                                                <span className={`pill text-[9px] font-bold uppercase border ${style.pill}`}>
                                                    {style.label}
                                                </span>
                                                {row.attested && row.summary && (
                                                    <span className="text-[10px] text-zinc-500 font-mono">
                                                        {row.summary.pass}/{row.summary.total}
                                                        {row.summary.warn > 0 && <span className="text-amber-400"> · {row.summary.warn} warn</span>}
                                                        {row.summary.fail > 0 && <span className="text-rose-400"> · {row.summary.fail} fail</span>}
                                                    </span>
                                                )}
                                                {row.filename && (
                                                    <code className="text-[9px] text-zinc-600 truncate">{row.filename}</code>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-1 shrink-0">
                                                <span
                                                    role="button"
                                                    tabIndex={0}
                                                    onClick={(e) => { e.stopPropagation(); copyUatUrl(row.iter); }}
                                                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); copyUatUrl(row.iter); } }}
                                                    data-testid={`preflight-attestation-row-${row.iter}-copy-url`}
                                                    className="text-[9px] text-cyber hover:underline cursor-pointer"
                                                >
                                                    copy URL
                                                </span>
                                                {isOpen ? <ChevronDown className="w-3.5 h-3.5 text-zinc-500" /> : <ChevronRight className="w-3.5 h-3.5 text-zinc-500" />}
                                            </div>
                                        </button>
                                        {isOpen && (
                                            <div className="px-2 pb-2 pt-1 border-t border-zinc-800" data-testid={`preflight-attestation-row-${row.iter}-detail`}>
                                                {!row.attested && (
                                                    <p className="text-[10px] text-zinc-500 italic">
                                                        No Pre-flight block in this UAT file. The agent did not attest pre-deploy checks for this iter.
                                                    </p>
                                                )}
                                                {row.attested && row.items.length > 0 && (
                                                    <ul className="space-y-0.5">
                                                        {row.items.map((it, idx) => (
                                                            <li
                                                                key={idx}
                                                                data-testid={`preflight-attestation-row-${row.iter}-item-${idx}`}
                                                                data-status={it.status}
                                                                className="flex items-start gap-1.5 text-[10px] text-zinc-300"
                                                            >
                                                                {it.status === "pass" && <CheckCircle2 className="w-3 h-3 text-emerald-400 shrink-0 mt-0.5" />}
                                                                {it.status === "warn" && <AlertTriangle className="w-3 h-3 text-amber-400 shrink-0 mt-0.5" />}
                                                                {it.status === "fail" && <XCircle className="w-3 h-3 text-rose-400 shrink-0 mt-0.5" />}
                                                                <span className="leading-relaxed">{it.text}</span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                )}
                                                {row.attested_at && (
                                                    <p className="text-[9px] text-zinc-600 font-mono mt-1">
                                                        file mtime: {new Date(row.attested_at).toLocaleString()}
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                        {data.rows.length > 10 && (
                            <div className="mt-2 flex items-center justify-between gap-2 text-[10px] text-zinc-500 border-t border-zinc-800 pt-2">
                                <span>
                                    Showing <span className="text-zinc-300 font-bold">10</span> of <span className="text-zinc-300 font-bold">{data.rows.length}</span> attestations.
                                </span>
                                <button
                                    type="button"
                                    onClick={() => setShowAll(true)}
                                    data-testid="preflight-attestation-view-all-btn"
                                    className="brutal-btn-ghost text-[10px] py-1 px-2"
                                >
                                    View all →
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
            {showAll && (
                <PreflightLedgerModal
                    rows={data?.rows || []}
                    onClose={() => setShowAll(false)}
                    onOpenIter={(it) => { setOpenIter(it); setShowAll(false); }}
                    copyUatUrl={copyUatUrl}
                />
            )}
        </CollapsibleCard>
    );
}

// ---------------------------------------------------------------------------
// PreflightLedgerModal — full-history popup for the pre-flight ledger.
// Lists every attestation row with verdict + filename. Clicking a row
// closes the modal and expands that iter in the inline ledger.
// ---------------------------------------------------------------------------
function PreflightLedgerModal({ rows, onClose, onOpenIter, copyUatUrl }) {
    return (
        <div
            onClick={onClose}
            data-testid="preflight-ledger-modal"
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 cursor-pointer"
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="brutal-card border-2 border-cyber/40 bg-zinc-950 max-w-2xl w-full max-h-[80dvh] overflow-hidden cursor-default flex flex-col"
            >
                <div className="flex items-center justify-between gap-2 p-3 border-b border-zinc-800">
                    <h3 className="font-display font-extrabold text-sm uppercase tracking-wide text-white">
                        Agent pre-flight ledger — all {rows.length} attestations
                    </h3>
                    <button
                        type="button"
                        onClick={onClose}
                        data-testid="preflight-ledger-modal-close"
                        className="brutal-btn-ghost text-[10px] py-1 px-2"
                    >
                        Close
                    </button>
                </div>
                <ul className="flex-1 overflow-y-auto p-2 space-y-1" data-testid="preflight-ledger-modal-rows">
                    {rows.map((row) => {
                        const style = VERDICT_STYLE[row.verdict] || VERDICT_STYLE.missing;
                        return (
                            <li
                                key={row.iter}
                                data-testid={`preflight-ledger-modal-row-${row.iter}`}
                                className="border border-zinc-800 bg-zinc-900/30 rounded-sm flex items-center gap-2 px-2 py-1.5"
                            >
                                <VerdictIcon verdict={row.verdict} />
                                <button
                                    type="button"
                                    onClick={() => onOpenIter(row.iter)}
                                    className="font-mono text-[11px] text-white font-bold hover:text-cyber hover:underline cursor-pointer"
                                >
                                    iter{row.iter}
                                </button>
                                <span className={`pill text-[9px] font-bold uppercase border ${style.pill}`}>
                                    {style.label}
                                </span>
                                {row.attested && row.summary && (
                                    <span className="text-[10px] text-zinc-500 font-mono">
                                        {row.summary.pass}/{row.summary.total}
                                        {row.summary.warn > 0 && <span className="text-amber-400"> · {row.summary.warn} warn</span>}
                                        {row.summary.fail > 0 && <span className="text-rose-400"> · {row.summary.fail} fail</span>}
                                    </span>
                                )}
                                {row.filename && (
                                    <code className="text-[9px] text-zinc-600 truncate flex-1 text-right">{row.filename}</code>
                                )}
                                <button
                                    type="button"
                                    onClick={() => copyUatUrl(row.iter)}
                                    className="text-[9px] text-cyber hover:underline shrink-0"
                                >
                                    copy URL
                                </button>
                            </li>
                        );
                    })}
                </ul>
            </div>
        </div>
    );
}



// ---------------------------------------------------------------------------
// Orphaned draft picks audit (iter84.56.4.1)
// ---------------------------------------------------------------------------
// Post-recovery audit: after the iter84.56.4 self-heal redeploys, this
// card surfaces every WCKO tournament that still has draft picks or
// participant rankings pointing at team UUIDs that no longer resolve
// (i.e., wiped placeholders from the pre-fix sync). Strictly read-only
// — the operator uses the affected_draft_ids + affected_participant_ids
// to manually outreach to players to re-rank.
